import React from "react";
import { usePlayerModal } from "../PlayerModalContext";
import { PlayerPickTrigger } from "./PlayerPickTrigger";

/** Tournament total vs par from ESPN `score.displayValue` (+4, -2, E, —). */
function parseTournamentScoreVsPar(displayValue) {
    if (displayValue == null) return null;
    const t = String(displayValue).trim();
    if (t === "" || t === "—" || t === "-") return null;
    if (t === "E" || t === "e") return 0;
    const n = parseInt(t, 10);
    return Number.isNaN(n) ? null : n;
}

/** Same semantics as avatar badge: under par red, E gray, over black. */
function tournamentScoreBadgeClass(n) {
    if (n == null || Number.isNaN(n)) {
        return "border border-masters-cream/30 bg-masters-pine/50 text-masters-cream/90";
    }
    if (n < 0) {
        return "border border-red-700/40 bg-red-600 text-white";
    }
    if (n === 0) {
        return "border border-gray-600/40 bg-gray-500 text-white";
    }
    return "border border-white/20 bg-black text-white";
}

/**
 * @typedef {{ id: string, displayName: string, holeThru: string, scoreDisplay: string, todayDetail: string | null, position: string, sortOrder: number }} PairingPlayer
 * @typedef {{ teeTimeIso: string | null, teeTimeLabel: string, players: PairingPlayer[] }} PairingGroup
 */

export function GroupsSection({ pairingGroups, competitionPeriod }) {
    const { openPlayer } = usePlayerModal();
    const roundNum =
        Number.isFinite(Number(competitionPeriod)) && Number(competitionPeriod) > 0
            ? Number(competitionPeriod)
            : 1;

    return (
        <section
            aria-label="Playing groups"
            className="groups space-y-4 text-masters-cream"
        >
            <h2 className="mb-1 border-b border-masters-moss/40 pb-2 text-xs font-semibold uppercase tracking-[0.2em] text-masters-goldmuted">
                Groups
            </h2>
            <p className="mb-3 text-[11px] leading-snug text-masters-cream/70">
                Tee times in UK (London). Grouped by shared tee time. Round{" "}
                {roundNum}.
            </p>
            {!pairingGroups?.length ? (
                <p className="text-sm text-masters-cream/80">
                    No pairing data available.
                </p>
            ) : (
                pairingGroups.map((group, idx) => (
                    <div
                        key={group.teeTimeIso ?? `tbd-${idx}`}
                        className="rounded-xl border border-masters-moss/35 bg-masters-pine/30 p-4 shadow-md shadow-black/10"
                    >
                        <div className="mb-3 border-b border-masters-moss/25 pb-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-masters-goldmuted">
                                Tee time
                            </div>
                            <div className="mt-0.5 text-base font-semibold tabular-nums text-masters-cream">
                                {group.teeTimeLabel}
                            </div>
                        </div>
                        <div className="space-y-2">
                            {group.players.map((p) => {
                                const vsPar = parseTournamentScoreVsPar(
                                    p.scoreDisplay,
                                );
                                return (
                                    <PlayerPickTrigger
                                        key={p.id}
                                        ariaLabel={`Player details: ${p.displayName}`}
                                        onOpen={() => openPlayer(p.displayName)}
                                    >
                                        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 rounded-lg border border-transparent px-1 py-2 text-left transition hover:border-masters-moss/40 hover:bg-masters-pine/40">
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium text-masters-cream">
                                                    {p.displayName}
                                                </div>
                                                {p.todayDetail ? (
                                                    <div className="mt-0.5 text-[11px] text-masters-cream/65">
                                                        Today: {p.todayDetail}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-1 text-sm tabular-nums">
                                                {p.position ? (
                                                    <span className="text-masters-cream/85">
                                                        {p.position}
                                                    </span>
                                                ) : null}
                                                <span className="text-masters-cream/90">
                                                    {p.holeThru}
                                                </span>
                                                <span
                                                    className={`inline-flex min-w-[2.25rem] items-center justify-end rounded-sm px-1.5 py-0.5 text-right text-xs font-bold tabular-nums ${tournamentScoreBadgeClass(vsPar)}`}
                                                >
                                                    {p.scoreDisplay}
                                                </span>
                                            </div>
                                        </div>
                                    </PlayerPickTrigger>
                                );
                            })}
                        </div>
                    </div>
                ))
            )}
        </section>
    );
}
